import { Injectable, Injector, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, switchMap, throwError, catchError } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { Auth, signOut } from '@angular/fire/auth';
import { AuthQuery } from '../state/auth.query';
import { environment } from '../../../environments/environment';
import { SnackbarService } from 'src/app/core/services/snackbar.service';
import { AuthService } from '../services/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { getFreshSharedAuthFlowMarker } from '../utils/auth-flow-marker';
import { reportForcedSignOut } from '../utils/auth-telemetry';

interface BearerMetadata {
  readonly lacksSid: boolean;
  readonly issuedAtSeconds: number | null;
}

const AUTH_ENDPOINTS = [
  '/api/v1/auth/sms/request',
  '/api/v1/auth/sms/verify',
  '/api/v1/auth/logout',
  '/api/v1/auth/heartbeat',
  // SSO endpoints: a 401/403 here means "no grant / SSO disabled", never a
  // broken session — must not trigger the interceptor's logout flow.
  '/api/v1/auth/sso/',
];

// Public order endpoints (magic-link guest access, X-Invitation-Token auth).
// No bearer token — injecting one would overwrite the header for a logged-in user
// visiting a public link — and no 401-triggered logout, since a 401 here reflects
// an invalid/expired invitation token, not a broken session.
const PUBLIC_PREFIX = '/api/v1/public/';

// Served by a Cloud Function behind a Hosting rewrite, not by the Go API host.
// It still needs the bearer token, but must NOT be redirected to environment.apiUrl
// or the call leaves the origin and the rewrite never sees it. When these routes
// move to Go, delete this constant and the branch below — the call sites are
// already on the final path.
const SAME_ORIGIN_PREFIXES = ['/api/v1/timesheets/'];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private auth = inject(Auth);
  private authQuery = inject(AuthQuery);
  private injector = inject(Injector);
  private transloco = inject(TranslocoService);

  private loggingOut = false;

  constructor() {
    this.authQuery.isLoggedIn$.subscribe({
      next: (loggedIn) => {
        if (loggedIn) {
          this.loggingOut = false;
        }
      },
      error: (err) => {
        console.error('[AuthInterceptor] isLoggedIn$ error:', err);
        Sentry.captureException(err, { tags: { component: 'AuthInterceptor', action: 'isLoggedIn$' } });
        this.loggingOut = false;
      },
    });
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith('/api/v1/')) {
      return next.handle(req);
    }

    // Prefix with API base URL (e.g., https://api.stage.tefter.com) for deployed environments,
    // except same-origin routes (see SAME_ORIGIN_PREFIXES) which must stay on this host.
    const sameOrigin = SAME_ORIGIN_PREFIXES.some((prefix) => req.url.startsWith(prefix));
    const localSso =
      environment.env === 'local' &&
      window.location.protocol === 'https:' &&
      ['business.local.tefter.com', 'tx.local.tefter.com'].includes(window.location.hostname) &&
      req.url.startsWith('/api/v1/auth/sso/');
    const apiBase = localSso
      ? `https://api.local.tefter.com${window.location.port ? ':' + window.location.port : ''}`
      : environment.apiUrl;
    const apiReq = apiBase && !sameOrigin ? req.clone({ url: apiBase + req.url }) : req;

    // Bootstrap authenticates with the HttpOnly cookie; an expired destination login must not block it.
    if (req.url === '/api/v1/auth/sso/bootstrap' || req.url.startsWith(PUBLIC_PREFIX)) {
      return next.handle(apiReq);
    }

    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return this.handleResponse(next.handle(apiReq), req.url, { lacksSid: false, issuedAtSeconds: null }, null);
    }

    const cached = this.authQuery.getIdToken();
    if (cached?.token && new Date(cached.expirationTime).getTime() - Date.now() > 60_000) {
      const markerAtSend = getFreshSharedAuthFlowMarker();
      const metadata = this.bearerMetadata(cached.claims, cached.issuedAtTime);
      return this.handleResponse(
        next.handle(apiReq.clone({ setHeaders: { Authorization: `Bearer ${cached.token}` } })),
        req.url,
        metadata,
        markerAtSend?.startedAt ?? null
      );
    }

    // Async fallback — cache resyncs on next authState event (~hourly token refresh)
    return from(currentUser.getIdToken()).pipe(
      catchError((error) => {
        console.error('[AuthInterceptor] getIdToken() failed:', error);
        reportForcedSignOut({
          trigger: 'token-refresh-failed',
          authCode: (error as { code?: string } | null)?.code ?? null,
        });
        signOut(this.auth).catch((err) => console.error('[AuthInterceptor] signOut failed:', err));
        return throwError(() => error);
      }),
      switchMap((token) => {
        const markerAtSend = getFreshSharedAuthFlowMarker();
        return this.handleResponse(
          next.handle(apiReq.clone({ setHeaders: { Authorization: `Bearer ${token}` } })),
          req.url,
          this.bearerMetadata(this.decodeJwtClaims(token)),
          markerAtSend?.startedAt ?? null
        );
      })
    );
  }

  private handleResponse(
    response$: Observable<HttpEvent<unknown>>,
    url: string,
    bearer: BearerMetadata,
    markerStartedAtAtSend: number | null
  ): Observable<HttpEvent<unknown>> {
    if (AUTH_ENDPOINTS.some((ep) => url.startsWith(ep))) {
      return response$;
    }

    return response$.pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.loggingOut) {
          const markerAtResponse = getFreshSharedAuthFlowMarker();
          if (
            this.shouldStandDownForLoginMarker(bearer, markerStartedAtAtSend) ||
            this.shouldStandDownForLoginMarker(bearer, markerAtResponse?.startedAt ?? null)
          ) {
            // The login flow owns recovery; logging out an in-flight stale bearer would clear shared persistence.
            return throwError(() => error);
          }
          const authService = this.injector.get(AuthService);
          // A pending org-switch resync means this 401 is the dead pre-switch
          // token, not a broken session: the scheduled reload owns recovery,
          // and logging out here would clear the SHARED Firebase persistence
          // mid-switch (the 2026-08-24 multi-tab logout).
          if (authService.isOrgSwitchResyncPending()) {
            return throwError(() => error);
          }
          this.loggingOut = true;
          reportForcedSignOut({ trigger: 'interceptor-401' });
          const snackbar = this.injector.get(SnackbarService);
          snackbar.error(this.transloco.translate('auth.sessionExpired'));
          authService.logOut().catch((err) => {
            console.error('[AuthInterceptor] logOut failed after 401:', err);
            Sentry.captureException(err, { tags: { component: 'AuthInterceptor', action: '401-logout' } });
            this.loggingOut = false;
          });
        }
        return throwError(() => error);
      })
    );
  }

  private bearerMetadata(claims: Record<string, unknown>, issuedAtTime?: string): BearerMetadata {
    const sid = claims['sid'];
    const iat = claims['iat'];
    let issuedAtSeconds = typeof iat === 'number' ? iat : typeof iat === 'string' ? Number(iat) : NaN;
    if (!Number.isFinite(issuedAtSeconds) && issuedAtTime) {
      issuedAtSeconds = new Date(issuedAtTime).getTime() / 1000;
    }
    return {
      lacksSid: typeof sid !== 'string' || sid.length === 0,
      issuedAtSeconds: Number.isFinite(issuedAtSeconds) ? issuedAtSeconds : null,
    };
  }

  private shouldStandDownForLoginMarker(bearer: BearerMetadata, markerStartedAt: number | null): boolean {
    if (markerStartedAt === null) return false;
    return bearer.lacksSid || (bearer.issuedAtSeconds !== null && bearer.issuedAtSeconds < markerStartedAt / 1000);
  }

  private decodeJwtClaims(token: string): Record<string, unknown> {
    try {
      const payload = token.split('.')[1];
      if (!payload) return {};
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const claims: unknown = JSON.parse(atob(padded));
      return !!claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
}
